import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

/**
 * Header component for drawers.
 * Displays title, optional subtitle/icon, and a close button.
 *
 * @example
 * ```html
 * <app-drawer-header
 *   [title]="'Customer Details'"
 *   [subtitle]="'#12345'"
 *   [icon]="'person'"
 *   (close)="drawerRef.close()">
 * </app-drawer-header>
 * ```
 */
@Component({
  selector: 'app-drawer-header',
  standalone: true,
  imports: [MatButtonModule, MatIconModule, MatTooltipModule],
  template: `
    <div class="drawer-header">
      <div class="header-content">
        @if (svgIcon) {
          <mat-icon class="header-icon" [svgIcon]="svgIcon"></mat-icon>
        } @else if (icon) {
          <mat-icon class="header-icon">{{ icon }}</mat-icon>
        }
        <div class="header-text">
          <h2 class="header-title">{{ title }}</h2>
          @if (subtitle) {
            <span class="header-subtitle">{{ subtitle }}</span>
          }
        </div>
      </div>
      <button mat-icon-button (click)="close.emit()" matTooltip="Cerrar" class="close-button">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  `,
  styles: [
    `
      .drawer-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 1rem 1.5rem;
        border-bottom: 1px solid rgba(0, 0, 0, 0.12);
        background: white;
        min-height: 64px;
      }

      .header-content {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        min-width: 0;
        flex: 1;
      }

      .header-icon {
        color: rgba(var(--fuse-primary-rgb), 1);
        flex-shrink: 0;
      }

      .header-text {
        display: flex;
        flex-direction: column;
        min-width: 0;
      }

      .header-title {
        margin: 0;
        font-size: 1.125rem;
        font-weight: 600;
        line-height: 1.4;
        color: rgba(0, 0, 0, 0.87);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .header-subtitle {
        font-size: 0.75rem;
        color: rgba(0, 0, 0, 0.54);
        line-height: 1.4;
      }

      .close-button {
        flex-shrink: 0;
        margin-left: 0.5rem;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DrawerHeaderComponent {
  /** Title displayed in the header */
  @Input() title: string = '';

  /** Optional subtitle displayed below the title */
  @Input() subtitle?: string;

  /** Optional Material icon name displayed before the title */
  @Input() icon?: string;

  /** Optional SVG icon name (e.g. 'heroicons_outline:funnel') */
  @Input() svgIcon?: string;

  /** Emits when the close button is clicked */
  @Output() close = new EventEmitter<void>();
}
